export type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export const FRAME_W = 1920;
export const FRAME_H = 1080;
export const PREVIEW_W = 960;
export const PREVIEW_H = 540;
export const FRAME_ASPECT = FRAME_W / FRAME_H;
export const MIN_CROP_W = 480;
export const MIN_CROP_H = Math.round(MIN_CROP_W / FRAME_ASPECT);
export const FULL_FRAME_CROP_RECT: Rect = {
  x: 0,
  y: 0,
  w: FRAME_W,
  h: FRAME_H,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function clampCropRect(rect: Rect): Rect {
  let width = Math.max(MIN_CROP_W, Math.round(rect.w));
  let height = Math.max(MIN_CROP_H, Math.round(width / FRAME_ASPECT));

  if (height > FRAME_H) {
    height = FRAME_H;
    width = Math.round(height * FRAME_ASPECT);
  }
  if (width > FRAME_W) {
    width = FRAME_W;
    height = Math.round(width / FRAME_ASPECT);
  }

  const x = clamp(Math.round(rect.x), 0, FRAME_W - width);
  const y = clamp(Math.round(rect.y), 0, FRAME_H - height);

  return { x, y, w: width, h: height };
}

export function createInsetCropRect(scale = 0.85): Rect {
  const safeScale = clamp(scale, MIN_CROP_W / FRAME_W, 1);
  let width = Math.round(FRAME_W * safeScale);
  let height = Math.round(width / FRAME_ASPECT);

  if (height > FRAME_H) {
    height = FRAME_H;
    width = Math.round(height * FRAME_ASPECT);
  }

  return clampCropRect({
    x: Math.round((FRAME_W - width) / 2),
    y: Math.round((FRAME_H - height) / 2),
    w: width,
    h: height,
  });
}

export function isFullFrameCropRect(rect: Rect): boolean {
  return (
    rect.x === FULL_FRAME_CROP_RECT.x &&
    rect.y === FULL_FRAME_CROP_RECT.y &&
    rect.w === FULL_FRAME_CROP_RECT.w &&
    rect.h === FULL_FRAME_CROP_RECT.h
  );
}

export function frameRectToPreviewRect(
  rect: Rect,
  previewW = PREVIEW_W,
  previewH = PREVIEW_H,
): Rect {
  const scaleX = previewW / FRAME_W;
  const scaleY = previewH / FRAME_H;
  return {
    x: rect.x * scaleX,
    y: rect.y * scaleY,
    w: rect.w * scaleX,
    h: rect.h * scaleY,
  };
}

export function previewRectToFrameRect(
  rect: Rect,
  previewW = PREVIEW_W,
  previewH = PREVIEW_H,
): Rect {
  const scaleX = FRAME_W / previewW;
  const scaleY = FRAME_H / previewH;
  return clampCropRect({
    x: rect.x * scaleX,
    y: rect.y * scaleY,
    w: rect.w * scaleX,
    h: rect.h * scaleY,
  });
}

export function getCoverSourceRect(
  sourceW: number,
  sourceH: number,
  targetW: number,
  targetH: number,
): Rect {
  const sourceAspect = sourceW / sourceH;
  const targetAspect = targetW / targetH;

  let x = 0;
  let y = 0;
  let w = sourceW;
  let h = sourceH;

  if (sourceAspect > targetAspect) {
    w = sourceH * targetAspect;
    x = (sourceW - w) / 2;
  } else if (sourceAspect < targetAspect) {
    h = sourceW / targetAspect;
    y = (sourceH - h) / 2;
  }

  return { x, y, w, h };
}

export function getCropSourceRect(
  video: HTMLVideoElement,
  cropRect: Rect = FULL_FRAME_CROP_RECT,
): Rect | null {
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return null;
  }

  const coverRect = getCoverSourceRect(
    video.videoWidth,
    video.videoHeight,
    FRAME_W,
    FRAME_H,
  );
  const scaleX = coverRect.w / FRAME_W;
  const scaleY = coverRect.h / FRAME_H;

  return {
    x: coverRect.x + cropRect.x * scaleX,
    y: coverRect.y + cropRect.y * scaleY,
    w: cropRect.w * scaleX,
    h: cropRect.h * scaleY,
  };
}

export function drawVideoFrame(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  targetW: number,
  targetH: number,
  cropRect: Rect = FULL_FRAME_CROP_RECT,
): void {
  const sourceRect = getCropSourceRect(video, cropRect);
  if (!sourceRect) return;

  ctx.clearRect(0, 0, targetW, targetH);
  ctx.drawImage(
    video,
    sourceRect.x,
    sourceRect.y,
    sourceRect.w,
    sourceRect.h,
    0,
    0,
    targetW,
    targetH,
  );
}
